import React, { type JSX } from 'react';
import EnhancedMarkdown from '../../../resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import Head from 'next/head';
import useGame from '../../../resources/js/contexts/useGame';
import enhancedMarkdownOverrides from '../../helpers/enhancedMarkdownOverrides';
import Tile from '../../../resources/js/components/Tile/Tile';
import ArticleTitle from '../../../resources/js/components/ArticleTitle/ArticleTitle';
import VerticalContent from '../../../resources/js/components/VerticalContent/VerticalContent';
import styles from './Article.module.scss';
import renderEnhancedMarkdownToString from '../../../resources/js/components/EnhancedMarkdown/helpers/renderEnhancedMarkdownToString';
import convertImageUrlToOptimizedImageUrl from '../../helpers/convertImageUrlToOptimizedImageUrl';
import TableOfContents from '../TableOfContents/TableOfContents';
import useSeriesOfArticles from '../../contexts/useSeriesOfArticles';
import EnhancedMarkdownCenter from '../../../resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownCenter';
import HorizontalContent from '../../../resources/js/components/HorizontalContent/HorizontalContent';
import HorizontalRule from '../../../resources/js/components/HorizontalRule/HorizontalRule';
import FormattedDateTime from '../../../resources/js/components/FormattedDateTime/FormattedDateTime';
import type { ArticleViewModel } from '../../../resources/js/typings/viewModels/article';
import Body from '../../../resources/js/components/Body/Body';
import Advertisements from '../Advertisements/Advertisements';
import cx from 'classnames';
import { ArchonAdPlacement } from '../ArchonAdPlacement/ArchonAdPlacement';
import { ArchonAdPlacementWithCloseButton } from '../ArchonAdPlacementWithCloseButton/ArchonAdPlacementWithCloseButton';
import useShowAdContainers from '../../hooks/useShowAdContainers';

type ArticleProps = ArticleViewModel & {
  gameLabel: string;
};

function Article({
  title,
  titleImageUrl,
  description,
  body,
  sidebar,
  preventIndexing,
  articleTags,
  publishedAt,
  updatedAt,
  featuredImageUrl,
  gameLabel,
}: ArticleProps): JSX.Element {
  const game = useGame();
  const showAdContainers = useShowAdContainers();
  const { nextLink, previousLink } = useSeriesOfArticles();

  const renderedTitle = renderEnhancedMarkdownToString(title, game);
  const renderedDescription =
    renderEnhancedMarkdownToString(description, game) + ` (${gameLabel})`;

  return (
    <>
      <Head>
        <title>{`${renderedTitle} | Archon (${gameLabel})`}</title>
        <meta property='og:title' key='og:title' content={renderedTitle} />
        <meta
          name='description'
          key='description'
          content={renderedDescription}
        />
        <meta
          property='og:description'
          key='og:description'
          content={renderedDescription}
        />
        {preventIndexing && (
          <>
            <meta name='robots' content='noindex' />
            <meta name='googlebot' content='noindex' />
          </>
        )}
        <meta property='og:type' key='og:type' content='article' />
        <meta property='article:author' content={game.siteTitle} />
        {articleTags.map((articleTag) => (
          <meta
            key={articleTag.slug}
            property='article:tag'
            content={articleTag.name}
          />
        ))}
        {publishedAt && (
          <meta property='article:published_time' content={publishedAt} />
        )}
        {updatedAt && (
          <meta property='article:modified_time' content={updatedAt} />
        )}
        {featuredImageUrl && (
          <>
            <meta
              key='og:image'
              property='og:image'
              content={featuredImageUrl}
            />
            <meta property='twitter:image' content={featuredImageUrl} />
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={renderedTitle} />
          </>
        )}
      </Head>
      <article>
        <VerticalContent>
          <ArticleTitle
            title={renderedTitle}
            backgroundImageUrl={convertImageUrlToOptimizedImageUrl(
              titleImageUrl,
              384,
              50,
            )}
            isSlim
          />
          <VerticalContent className={styles.body}>
            <div
              className={cx({
                [styles.grid]: true,
                [styles.gridWithAds]: showAdContainers,
              })}
            >
              <section className={styles.leftHandSideBar}>
                <EnhancedMarkdown
                  markdown={sidebar}
                  overrides={enhancedMarkdownOverrides}
                />
                <Body
                  className={cx({
                    [styles.leftHandSideBarTableOfContents]: !showAdContainers,
                  })}
                >
                  <TableOfContents
                    maximumHeadingLevel={3}
                    minimumNumberOfHeadings={3}
                  />
                  <LastUpdatedDate timestamp={updatedAt} />
                </Body>
              </section>
              <section className={styles.content}>
                <VerticalContent className={styles.tileContainer}>
                  <Tile
                    style={'flat'}
                    padding={'narrow'}
                    className={styles.inlineTableOfContents}
                  >
                    <nav aria-label={'Table of Contents'}>
                      <Body>
                        <details>
                          <summary>
                            <span>Table of Contents</span>
                          </summary>
                          <EnhancedMarkdown
                            markdown={sidebar}
                            overrides={enhancedMarkdownOverrides}
                          />
                          <TableOfContents
                            maximumHeadingLevel={3}
                            minimumNumberOfHeadings={3}
                          />
                          <LastUpdatedDate timestamp={updatedAt} />
                        </details>
                      </Body>
                    </nav>
                  </Tile>
                  <Tile
                    style={'flat'}
                    contentCssStyle={{ overflowX: 'hidden' }}
                  >
                    <VerticalContent>
                      <EnhancedMarkdown
                        markdown={body}
                        overrides={enhancedMarkdownOverrides}
                      />
                      {(nextLink || previousLink) && (
                        <Body>
                          <VerticalContent>
                            <HorizontalRule color={'light-gray'} />
                            {nextLink && (
                              <EnhancedMarkdownCenter>
                                <HorizontalContent>
                                  <span>Next →</span>
                                  <span>{nextLink}</span>
                                </HorizontalContent>
                              </EnhancedMarkdownCenter>
                            )}
                            {previousLink && (
                              <EnhancedMarkdownCenter>
                                <HorizontalContent>
                                  <span>{previousLink}</span>
                                  <span>← Previous</span>
                                </HorizontalContent>
                              </EnhancedMarkdownCenter>
                            )}
                          </VerticalContent>
                        </Body>
                      )}
                    </VerticalContent>
                  </Tile>
                </VerticalContent>
              </section>
              <section className={styles.rightHandSideBar}>
                <Body className={styles.rightHandSideBarContent}>
                  {showAdContainers ? (
                    <Advertisements showChrome={true}>
                      <ArchonAdPlacement location={'sideRailWide'} />
                    </Advertisements>
                  ) : (
                    <>
                      <TableOfContents
                        maximumHeadingLevel={3}
                        minimumNumberOfHeadings={3}
                      />
                      <LastUpdatedDate timestamp={updatedAt} />
                    </>
                  )}
                </Body>
              </section>
              {showAdContainers && (
                <span className={styles.stickyFooterAd}>
                  <ArchonAdPlacementWithCloseButton>
                    <ArchonAdPlacement location={'stickyFooter'} />
                  </ArchonAdPlacementWithCloseButton>
                </span>
              )}
            </div>
          </VerticalContent>
        </VerticalContent>
      </article>
    </>
  );
}

type LastUpdatedDateProps = {
  timestamp?: string;
};

function LastUpdatedDate({ timestamp }: LastUpdatedDateProps) {
  return timestamp ? (
    <div className={styles.articleLastUpdated}>
      <EnhancedMarkdownCenter>
        Last updated:{' '}
        <FormattedDateTime
          timestamp={timestamp}
          dateStyle={'long'}
          locales={'en-US'}
        />
      </EnhancedMarkdownCenter>
    </div>
  ) : null;
}

export default Article;
